import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@common/components/container';
import { TopNavigationBar } from '@common/components/navigation-bar';
import Card from '@common/components/card';
import { CardContent } from '@common/components/card/card-content';
import { TextareaInputHookForm as TextareaInput } from '@common/components/form/inputs/textarea/textarea';
import { useForm } from 'react-hook-form';
import { Button } from '@common/components/button';
import { FileInput } from '@common/components/form/inputs/file';
import {
  FormLabelHookForm,
  Group,
  LanguageInputHookForm,
  Row,
} from '@common/components/form';
import { Toggle } from '@common/components/form/inputs/toggle';
import { RadioButton } from '@common/components/radio-button';
import { OverviewSection } from '@common/components/overview';
import { useHistory, useParams, useLocation } from 'react-router';
import generateOutline from '../../actions/genarate-outline';
import { useThunkDispatch } from '@common/hooks/redux/use-thunk-dispatch';
import { AlertService } from '@common/services/alert';
import { CourseGenerationLength, CourseGenerationTone, FormData } from './types';
import { TextInputHookForm } from '@common/components/form/inputs/text';

import { ECourseTypes } from '@modules/learning/definitions';
import { useChangeFileInput } from '@common/components/form/inputs/file/hooks';
import type { Attachment, FileInProgress } from '@common/types/objects';
import { AttachmentsHookForm } from '@common/components/form/attachments';
import useUnsavedChanges from '@common/hooks/use-unsaved';
import ConfirmButton from '@common/components/confirm-button';
import GenerateCourseModal from '@modules/learning/components/generate-course-modal/generate-course-model';
import getFile from '@modules/learning/actions/get-file';

interface GenAICardProps {
  children?: ReactNode;
  className?: string;
  header: string;
  subheader: string;
}

const GenAICard: React.FC<GenAICardProps> = ({
  children,
  className = '',
  header,
  subheader,
}) => {
  return (
    <Card containerClassName="tw-w-full">
      <CardContent
        className={`!tw-w-[500px] !tw-w-[90%] tw-flex tw-flex-col tw-gap-5 !tw-p-[32px] ${className}`}
      >
        <div className="tw-flex tw-flex-col tw-gap-[6px]">
          <div className="tw-font-inter tw-text-xl tw-font-semibold">
            {header}
          </div>
          <div className="tw-font-inter tw-text-base tw-font-normal tw-text-gray-muted">
            {subheader}
          </div>
        </div>

        {children}
      </CardContent>
    </Card>
  );
};

const GenAIContainer: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useThunkDispatch();
  const { files, onRemove, onChange, setFiles } = useChangeFileInput();
  const params = useParams<{ type: ECourseTypes }>();
  const location = useLocation();

  const topicRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  const {
 control, formState, handleSubmit, reset, watch, setValue, getValues, trigger
} =
    useForm<FormData>({
      mode: 'all',
      defaultValues: {
        topic: null,
        fileId: files ? files[0]?.id : null,
        industry: null,
        language: null,
        includeQuiz: true,
        length: 'short',
        toneOfVoice: 'professional_formal',
        learningGoal: null,
        type:
          params.type === ECourseTypes.ONBOARDING
            ? ECourseTypes.ONBOARDING
            : ECourseTypes.ACADEMY,
      },
    });

  useEffect(() => {
    const settings = (location.state as any)?.generationSettings;

    if (settings?.topic) setValue('topic', settings?.topic);
    if (settings?.industry) setValue('industry', settings?.industry);
    if (settings?.language) setValue('language', settings?.language);
    if (settings?.length) setValue('length', settings?.length as any);
    if (settings?.toneOfVoice) setValue('toneOfVoice', settings?.toneOfVoice as any);
    if (settings?.learningGoal) setValue('learningGoal', settings?.learningGoal);
    if (settings?.includeQuiz) setValue('includeQuiz', settings?.includeQuiz);
    if (settings?.fileId) {
setValue('fileId', settings?.fileId);
    }
  }, [location.state, setValue]);

  useEffect(() => {
    const settings = (location.state as any)?.generationSettings;

    if (settings?.fileId) {
      const fetchFile = async (fileId: string) => {
        const result = await dispatch(getFile(fileId));
        if (result && result.file && result.file.data) {
          setFiles([result.file.data]);
        }
      };
      fetchFile(settings?.fileId);
    }
  }, [location.state, setFiles]);

  useEffect(() => {
    if (files && files.length > 0) {
      setValue('fileId', files[0].id);
    } else {
      setValue('fileId', null);
    }
  }, [files, setValue]);

  const watchedLength = watch('length');
  const watchedTone = watch('toneOfVoice');

  const { isSubmitting, isDirty, errors } = formState;

  const { handleConfirmNavigation, handleCancelNavigation } = useUnsavedChanges(
    { isDirty }
  );

  const lengthOptionsMap = ['short', 'medium', 'long'];
  const toneOptionsMap = [
    'professional_formal',
    'conversational_friendly',
    'motivational_energetic',
    'practical_direct',
    'supportive_encouraging'];

  const getLengthOptions = () => {
    return lengthOptionsMap.map((value) => ({
      label: t(`learning:gen_ai_length_${value}`),
      value,
      description: t(
        `learning:gen_ai_length_${value}_description`
      ),
    }));
  };

  const getToneOptions = () => {
    return toneOptionsMap.map((value) => ({
      label: t(`learning:gen_ai_tone_${value}`),
      value,
      description: t(
        `learning:gen_ai_tone_${value}_description`
      ),
    }));
  };

  const lengthOptions = getLengthOptions();
  const toneOptions = getToneOptions();

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        trigger(['topic', 'fileId', 'language']);
        const filteredData = Object.entries(data).reduce(
          (acc, [key, value]) => {
            if (value !== null && value !== '') {
              (acc as any)[key] = value;
            }
            return acc;
          },
          {} as Partial<FormData>
        );

        const result = await dispatch(generateOutline(filteredData));
        if (result?.item) {
          AlertService.success(t('learning:gen_ai_success_message'));
          reset();
          history.push(`/admin/learning/${params.type}/course/course-outline`);
        }
      } catch (error) {
        AlertService.error(t('learning:gen_ai_error_message'));
      }
    },
    [dispatch, history, params.type, reset, t, trigger]
  );

  const handleCreateOutline = () => {
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    if (errors.topic && errors.language) {
      topicRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors.topic]);

  useEffect(() => {
    if (errors.language && !errors.topic) {
      languageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors.language]);

  return (
    <Container name="GenAI">
      <TopNavigationBar
        breadcrumbs={[
          { name: t('learning:breadcrumb_learning_environment') },
          params.type === ECourseTypes.ONBOARDING
            ? {
                name: t('learning:breadcrumb_onboarding'),
                path: '/admin/learning/onboarding',
              }
            : {
                name: t('learning:breadcrumb_academy'),
                path: '/admin/learning/academy',
              },
          { name: t('learning:gen_ai_title') },
        ]}
        title={t('learning:gen_ai_title')}
        action={
          <>
            <ConfirmButton
              title={t('learning:gen_ai_unsaved_changes_title')}
              description={t('learning:gen_ai_unsaved_changes_description')}
              onCancel={handleCancelNavigation}
              onConfirm={() => handleConfirmNavigation(`/admin/learning/${params.type}`)}
              confirmText={t('learning:gen_ai_unsaved_changes_confirm')}
              cancelText={t('learning:gen_ai_unsaved_changes_cancel')}
              danger
            >
              <Button size="large">{t('forms:go_back')}</Button>
            </ConfirmButton>
            <GenerateCourseModal
              isCreatingCourse={isSubmitting}
              isGenerateOutline
            >
              <Button
                onClick={handleCreateOutline}
                type="blue"
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                {t('learning:gen_ai_create_outline')}
              </Button>
            </GenerateCourseModal>
          </>
        }
      />
      <Container.Content className="tw-gap-6 !tw-w-[650px]">
        <GenAICard
          header={t('learning:gen_ai_general_header')}
          subheader={t('learning:gen_ai_general_subheader')}
        >
          <div ref={topicRef}>
            <TextareaInput
              className="!tw-h-[100px]"
              name="topic"
              control={control}
              placeholder={t('learning:gen_ai_topic_placeholder')}
              minRows={3}
              rules={{
                validate: () => {
                  const values = getValues();
                  const hasTopic = values.topic && values.topic.trim().length > 0;
                  const hasFile = values.fileId && values.fileId.length > 0;

                  if (!hasTopic && !hasFile) {
                    return 'Either topic or file must be provided';
                  }
                  return true;
                },
              }}
            />
            {/* <div className="tw-flex tw-border-gray-neutral-50 tw-flex-row tw-items-center tw-gap-2 tw-px-[12px] tw-pt-[12px] tw-pb-[16px] tw-bg-gray-50">
              <div className="tw-flex tw-flex-1 tw-flex-row">
                <div
                  className="tw-w-5 tw-h-5 tw-mr-4 tw-rounded tw-bg-no-repeat"
                  style={{
                    backgroundImage: `url(${'/static/images/bulb-on.png'})`,
                  }}
                />
                <div className="tw-text-[#71717A] tw-items-center tw-font-inter tw-text-base tw-font-medium">
                  {t('learning:gen_ai_need_inspiration')}
                </div>
              </div>
              <span>{t('learning:gen_ai_clear')}</span>
              <Button className="!tw-bg-white !tw-border-[1px] !tw-border-solid !tw-border-[#E4E4E7] tw-text-base tw-font-medium tw-font-inter !tw-text-[#52525B]">
                {t('learning:gen_ai_generate_idea')}
              </Button>
            </div> */}
          </div>
          <div className="tw-flex tw-items-center">
            <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />
            <span className="tw-mx-4 tw-text-gray-400 tw-font-medium">
              {t('learning:gen_ai_or')}
            </span>
            <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />
          </div>
          <FileInput
            className="tw-bg-gray-neutral-50"
            maxFileSize={500}
            value={files[0]}
            onChange={onChange}
            acceptedExtLabel="PDF"
            accept=".pdf,application/pdf"
          />
          <TextInputHookForm
            name="fileId"
            control={control}
            style={{ display: 'none' }}
          />
          <AttachmentsHookForm
            fields={files as (Attachment & FileInProgress)[]}
            remove={onRemove}
          />
        </GenAICard>

        <GenAICard
          header={t('learning:gen_ai_course_settings_header')}
          subheader={t('learning:gen_ai_course_settings_subheader')}
        >
          <Row>
            <Group>
              <FormLabelHookForm
                control={control}
                name="industry"
                text={t('learning:gen_ai_industry_label')}
                className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
              />
              <TextInputHookForm
                name="industry"
                control={control}
                placeholder={t('learning:gen_ai_industry_placeholder')}
              />
            </Group>
          </Row>
          <Row>
            <Group>
              <div ref={languageRef}>
                <FormLabelHookForm
                  required
                  control={control}
                  name="language"
                  text={t('learning:gen_ai_language_label')}
                  className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
                />
                <LanguageInputHookForm
                  name="language"
                  control={control}
                  translations
                  width="100%"
                  rules={{
                    validate: () => {
                      const values = getValues();
                      if (!values.language) {
                        return 'Language is required';
                      }
                      return true;
                    },
                  }}
                />
              </div>
            </Group>
          </Row>
          <div className="tw-h-[1px] tw-bg-gray-neutral-50" />
          <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <div className="tw-text-base tw-text-inter tw-font-medium tw-text-gray-dark-900">
                {t('learning:gen_ai_include_quiz_label')}
              </div>
              <div className="tw-text-base tw-text-inter tw-font-normal tw-text-gray-text">
                {t('learning:gen_ai_include_quiz_description')}
              </div>
            </div>
            <Toggle
              value={watch('includeQuiz')}
              onChange={(value) => setValue('includeQuiz', value)}
            />
          </div>
        </GenAICard>

        <GenAICard
          header={t('learning:gen_ai_length_header')}
          subheader={t('learning:gen_ai_length_subheader')}
        >
          <div className="tw-flex tw-flex-col tw-gap-4">
            {lengthOptions.map((option) => (
              <OverviewSection key={option.value} className="!tw-mt-0">
                <div
                  className="tw-flex tw-flex-col tw-gap-4 tw-cursor-pointer"
                  onClick={() => setValue('length', option.value as CourseGenerationLength)}
                >
                  <div
                    key={option.value}
                    className="tw-flex tw-flex-col tw-gap-[2px]"
                  >
                    <div className="tw-flex tw-flex-row tw-gap-2">
                      <RadioButton
                        labelClassName="tw-text-base tw-text-inter tw-font-medium tw-text-gray-dark-900 tw-h-[20px]"
                        label={option.label}
                        value={option.value}
                        currentValue={watchedLength}
                        onChange={(value) => setValue('length', value)}
                      />
                    </div>
                    <div className="tw-pl-[26px] tw-text-base tw-text-inter tw-font-normal tw-text-gray-text">
                      {option.description}
                    </div>
                  </div>
                </div>
              </OverviewSection>
            ))}
          </div>
        </GenAICard>

        <GenAICard
          header={t('learning:gen_ai_tone_header')}
          subheader={t('learning:gen_ai_tone_subheader')}
        >
          <div className="tw-flex tw-flex-col tw-gap-4">
            {toneOptions.map((option) => (
              <OverviewSection key={option.value} className="!tw-mt-0">
                <div
                  className="tw-flex tw-flex-col tw-gap-4 tw-cursor-pointer"
                  onClick={() => setValue('toneOfVoice', option.value as CourseGenerationTone)}
                >
                  <div className="tw-flex tw-flex-col tw-gap-[2px]">
                    <div className="tw-flex tw-flex-row tw-gap-2">
                      <RadioButton
                        labelClassName="tw-text-base tw-text-inter tw-font-medium tw-text-gray-dark-900 tw-h-[20px]"
                        label={option.label}
                        value={option.value}
                        currentValue={watchedTone}
                        onChange={(value) => setValue('toneOfVoice', value)}
                      />
                    </div>
                    <div className="tw-pl-[26px] tw-text-base tw-text-inter tw-font-normal tw-text-gray-text">
                      {option.description}
                    </div>
                  </div>
                </div>
              </OverviewSection>
            ))}
          </div>
        </GenAICard>

        <GenAICard
          header={t('learning:gen_ai_learning_goal_header')}
          subheader={t('learning:gen_ai_learning_goal_subheader')}
        >
          <div>
            <TextareaInput
              className="!tw-h-[100px]"
              name="learningGoal"
              control={control}
              placeholder={t('learning:gen_ai_learning_goal_placeholder')}
              minRows={3}
            />
            {/* <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-px-[12px] tw-pt-[12px] tw-pb-[16px] tw-bg-gray-50">
              <div className="tw-flex tw-flex-1 tw-flex-row">
                <div
                  className="tw-w-5 tw-mr-4 tw-rounded tw-bg-no-repeat"
                  style={{
                    backgroundImage: `url(${'/static/images/bulb-on.png'})`,
                  }}
                />
                <div className="tw-text-[#71717A] tw-items-center tw-font-inter tw-text-base tw-font-medium">
                  {t('learning:gen_ai_need_inspiration')}
                </div>
              </div>
              <span>{t('learning:gen_ai_clear')}</span>
              <Button className="!tw-bg-white !tw-border-[1px] !tw-border-solid !tw-border-[#E4E4E7] tw-text-base tw-font-medium tw-font-inter !tw-text-[#52525B]">
                {t('learning:gen_ai_generate_idea')}
              </Button>
            </div> */}
          </div>
        </GenAICard>
      </Container.Content>
    </Container>
  );
};

export default GenAIContainer;
